import React from 'react';
import { Scale, BookOpen, MapPin, Phone, Clock } from 'lucide-react';
import LLSEOMeta from './LLSEOMeta';

const Button = ({ children, className, size = "md", variant = "primary", href }) => {
  const sizeClasses = {
    lg: "px-5 py-2.5 text-lg",
    md: "px-4 py-2 text-base",
  };

  const variantClasses = {
    primary: "bg-blue-500 hover:bg-blue-600 text-white",
    outline: "border-2 border-white text-white hover:bg-white/10",
  };

  const baseClasses = `inline-block rounded-md font-medium transition-colors ${sizeClasses[size]} ${variantClasses[variant]} ${className || ''}`;

  if (href) {
    return (
      <a href={href} className={baseClasses}>
        {children}
      </a>
    );
  }

  return (
    <button className={baseClasses}>
      {children}
    </button>
  );
};

const Card = ({ children, className }) => (
  <div className={`rounded-lg ${className || ''}`}>
    {children}
  </div>
);

const LLandingPage = () => {
  // Schema defined as plain object — passed to LLSEOMeta as prop, rendered in Helmet (bot-visible)
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": "https://loopholelaw.com",
    "name": "LoopHoleLaw",
    "alternateName": "Loophole Law Firm Kollam",
    "description": "Leading law firm in Kollam, Kerala providing expert legal services in Criminal Law, Cyber Law, Civil Law, Property Law, and Family Law. Serving Kollam, Paravur, Karunagappally, and Punalur.",
    "url": "https://loopholelaw.com",
    "telephone": "+919539650421",
    "email": "loopholelawcontact@gmail.com",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1st Floor, Ganga Building, Opposite Kollam Collectorate",
      "addressLocality": "Kollam",
      "addressRegion": "Kerala",
      "postalCode": "691013",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "8.892817793613391",
      "longitude": "76.57290401478547"
    },
    "areaServed": [
      { "@type": "City", "name": "Kollam" },
      { "@type": "City", "name": "Paravur" },
      { "@type": "City", "name": "Karunagappally" },
      { "@type": "City", "name": "Punalur" }
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "13:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/loopholelaw",
      "https://www.linkedin.com/company/loopholelaw"
    ]
  };

  return (
    <>
      <LLSEOMeta 
        title="Best Lawyer in Kollam | LoopHoleLaw | കൊല്ലം അഭിഭാഷകൻ"
        description="Leading law firm in Kollam, Kerala. Expert legal services in Criminal, Cyber, Civil, Property & Family Law. 5+ years experience. Free consultation. കൊല്ലം നിയമ സേവനങ്ങൾ. Call: +91 9539650421"
        keywords="lawyer kollam, advocate kollam, property lawyer kollam, divorce lawyer kollam, criminal lawyer kollam, cyber lawyer kollam, കൊല്ലം അഭിഭാഷകൻ, kollam law firm, best lawyer in kollam, family court lawyer kollam, civil lawyer kollam"
        canonicalPath="/"
        schema={JSON.stringify(schemaMarkup)}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
        {/* Hero Section */}
        <header className="relative h-[85vh] flex items-center justify-center text-white px-4">
          <div className="absolute inset-0 bg-black/50" />
          
          <div className="relative z-10 text-center space-y-4 max-w-4xl">
            {/* Logo */}
            <div className="relative w-16 h-16 mx-auto mb-4">
              <BookOpen className="w-16 h-16 text-blue-400 absolute" />
              <Scale className="w-12 h-12 text-green-400 absolute bottom-0 right-0" />
            </div>
            
            {/* Main Heading - SEO Optimized for Kollam */}
            <h1 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-teal-400">
              Leading Law Firm in Kollam
            </h1>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-2">
              LoopHoleLaw
            </h2>
            
            {/* Malayalam Tagline */}
            <p className="text-xl md:text-2xl text-green-400 font-semibold">
              കൊല്ലത്തെ മികച്ച നിയമ സേവനങ്ങൾ
            </p>
            
            <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto">
              Expert Legal Services in <strong>Kollam, Kerala</strong> | Criminal, Cyber, Civil, Property & Family Law
            </p>
            
            {/* Quick Contact Info */}
            <div className="flex flex-wrap gap-4 justify-center items-center text-sm md:text-base mt-4">
              <a href="tel:+919539650421" className="flex items-center gap-2 text-blue-400 hover:text-blue-300">
                <Phone className="w-5 h-5" />
                <span>+91 9539650421</span>
              </a>
              <span className="text-slate-400">|</span>
              <div className="flex items-center gap-2 text-slate-300">
                <MapPin className="w-5 h-5" />
                <span>Kollam, Kerala</span>
              </div>
            </div>
            
            <div className="flex gap-3 justify-center mt-6">
              <Button size="lg" href="/consultation">
                Free Consultation
              </Button>
              <Button size="lg" variant="outline" href="/about">
                Learn More
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <p className="text-sm text-slate-400 mt-4">
              5+ Years Experience | Free Consultation | Malayalam & English Services
            </p>
          </div>
        </header>

        {/* Introduction Section - SEO Rich Content */}
        <section className="py-12 px-4 bg-slate-900/50">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-300 text-lg leading-relaxed">
              Welcome to <strong className="text-white">LoopHoleLaw</strong>, your trusted <strong className="text-white">legal partner in Kollam</strong>. 
              We are a leading <strong className="text-white">law firm in Kollam, Kerala</strong>, providing expert legal services 
              in Criminal Law, Cyber Law, Civil Law, Property Law, and Family Law. With over 5 years of 
              experience serving clients across <strong className="text-white">Kollam, Paravur, Karunagappally, and Punalur</strong>, 
              we are committed to delivering justice and protecting your rights.
            </p>
            <p className="text-green-400 mt-4 text-base">
              കൊല്ലം ജില്ലയിലെ മികച്ച നിയമ സേവനങ്ങൾ നൽകുന്ന LoopHoleLaw-ലേക്ക് സ്വാഗതം. 
              ക്രിമിനൽ, സൈബർ, സിവിൽ, പ്രോപ്പർട്ടി, കുടുംബം എന്നീ കേസുകളിൽ വിദഗ്ധ സഹായം.
            </p>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-3">
              Legal Services in Kollam
            </h2>
            <p className="text-slate-400 text-center mb-8">
              കൊല്ലത്തെ നിയമ സേവനങ്ങൾ
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  title: "Criminal Law",
                  malayalam: "ക്രിമിനൽ നിയമം",
                  description: "Expert defense and representation in criminal cases. Bail applications, anticipatory bail, and criminal defense in Kollam courts.",
                  keywords: "Criminal Lawyer Kollam, Bail Application"
                },
                {
                  title: "Cyber Law",
                  malayalam: "സൈബർ നിയമം",
                  description: "Protecting your digital rights and handling cybercrime cases. Online fraud, data theft, and cyber harassment cases.",
                  keywords: "Cyber Crime Lawyer Kollam"
                },
                {
                  title: "Civil Law",
                  malayalam: "സിവിൽ നിയമം",
                  description: "Resolving civil disputes, money recovery, and protecting your civil rights in Kollam civil courts.",
                  keywords: "Civil Lawyer Kollam, Dispute Resolution"
                },
                {
                  title: "Property Law",
                  malayalam: "പ്രോപ്പർട്ടി നിയമം",
                  description: "Property registration, disputes, documentation, and title verification at Kollam sub-registrar offices.",
                  keywords: "Property Lawyer Kollam, Land Registration"
                },
                {
                  title: "Family Law",
                  malayalam: "കുടുംബ നിയമം",
                  description: "Divorce, custody, maintenance, and family court representation in Kollam family court.",
                  keywords: "Divorce Lawyer Kollam, Family Court"
                },
                {
                  title: "Consumer Court",
                  malayalam: "ഉപഭോക്തൃ കോടതി",
                  description: "Consumer complaints and disputes in Kollam consumer court. Product defects, service issues.",
                  keywords: "Consumer Court Lawyer Kollam"
                }
              ].map((service, index) => (
                <Card key={index} className="bg-slate-800/50 border border-slate-700 hover:bg-slate-800/70 transition-all p-4">
                  <h3 className="text-lg font-semibold text-white mb-1">
                    {service.title}
                  </h3>
                  <p className="text-green-400 text-sm mb-2">
                    {service.malayalam}
                  </p>
                  <p className="text-slate-300 text-sm mb-2">
                    {service.description}
                  </p>
                  <p className="text-xs text-slate-500 italic">
                    {service.keywords}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas Section - Critical for Local SEO */}
        <section className="py-12 px-4 bg-slate-900">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-3">
              Our Service Areas in Kollam District
            </h2>
            <p className="text-slate-400 text-center mb-8">
              സേവന മേഖലകൾ
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto">
              {[
                { name: "Kollam City", malayalam: "കൊല്ലം നഗരം" },
                { name: "Paravur", malayalam: "പരവൂർ" },
                { name: "Karunagappally", malayalam: "കരുനാഗപ്പള്ളി" },
                { name: "Punalur", malayalam: "പുനലൂർ" },
                { name: "Chavara", malayalam: "ചാവറ" },
                { name: "Sasthamcotta", malayalam: "ശാസ്താംകോട്ട" },
                { name: "Kottarakkara", malayalam: "കോട്ടാരക്കര" },
                { name: "Anchal", malayalam: "ആഞ്ചൽ" }
              ].map((area, index) => (
                <div key={index} className="bg-slate-800/50 border border-slate-700 p-3 rounded-lg text-center hover:bg-slate-800/70 transition-all">
                  <p className="text-white font-medium text-sm">{area.name}</p>
                  <p className="text-green-400 text-xs mt-1">{area.malayalam}</p>
                </div>
              ))}
            </div>
            
            <p className="text-slate-300 text-center mt-6 text-sm">
              We proudly serve clients throughout <strong className="text-white">Kollam district</strong> including 
              all nearby areas. Visit our office or call for immediate legal assistance.
            </p>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
              Why Choose LoopHoleLaw in Kollam?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  title: "5+ Years Experience",
                  malayalam: "5+ വർഷത്തെ പരിചയം",
                  description: "Proven track record in Kollam courts"
                },
                {
                  title: "Free Consultation",
                  malayalam: "സൗജന്യ കൺസൾട്ടേഷൻ",
                  description: "Initial consultation at no cost"
                },
                {
                  title: "Malayalam & English",
                  malayalam: "മലയാളം & ഇംഗ്ലീഷ്",
                  description: "Bilingual legal services"
                },
                {
                  title: "Local Expertise",
                  malayalam: "പ്രാദേശിക വിദഗ്ധത",
                  description: "Deep knowledge of Kollam legal system"
                }
              ].map((feature, index) => (
                <div key={index} className="p-4 rounded-lg bg-gradient-to-br from-blue-500/10 to-teal-500/10 border border-slate-700">
                  <p className="text-base font-medium text-white mb-1">{feature.title}</p>
                  <p className="text-sm text-green-400 mb-2">{feature.malayalam}</p>
                  <p className="text-xs text-slate-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 px-4 bg-slate-800/50">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-2">
              Need Legal Help in Kollam?
            </h2>
            <p className="text-green-400 mb-4">
              കൊല്ലത്ത് നിയമ സഹായം വേണോ?
            </p>
            <p className="text-slate-300 mb-6 text-sm">
              Schedule a free consultation with our expert legal team today. 
              We serve clients across Kollam district.
            </p>
            
            {/* Contact Details */}
            <div className="bg-slate-900/50 rounded-lg p-6 mb-6 border border-slate-700">
              <div className="space-y-3">
                <a href="tel:+919539650421" className="flex items-center justify-center gap-3 text-blue-400 hover:text-blue-300 text-lg">
                  <Phone className="w-5 h-5" />
                  <span>+91 9539650421</span>
                </a>
                <div className="flex items-center justify-center gap-3 text-slate-300">
                  <MapPin className="w-5 h-5" />
                  <span>1st Floor, Ganga Building<br/>
                    Opposite Kollam Collectorate<br/>
                    Kollam, Kerala - 691013<br/>
                    India</span>
                </div>
                <div className="flex items-center justify-center gap-3 text-slate-300">
                  <Clock className="w-5 h-5" />
                  <span>Mon-Fri: 9 AM - 6 PM | Sat: 9 AM - 1 PM</span>
                </div>
              </div>
            </div>
            
            <Button size="lg" href="/contact">
              Contact Us Now
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default LLandingPage;